import dayjs from "dayjs";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import React, { useEffect } from "react";
import { MoreOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  Input,
  Table,
  Empty,
  Popover,
  Dropdown,
  Typography,
  Form,
  Modal,
  ConfigProvider,
  Radio,
} from "antd";

import { BatchNIBSS, Generic } from "components/Status";
import { ApiErrorTableComponent } from "components/ErrorHandler";

import { BatchSummaryDTO, BatchSummaryInput } from "models";
import {
  setApprover,
  updateLayout,
  setIsPushToNIBSSModalOpen,
  setIsRefreshStatusModalOpen,
  setIsApproveBulkBatchModalOpen,
  setIsApproveSingleBatchModalOpen,
  setIsRefreshSingleBatchStatusOpen,
  setIsPushSingleBatchToNIBSSModalOpen,
  setIsChangeBulkBatchApproverModalOpen,
  setIsChangeSingleBatchApproverModalOpen,
} from "pages/TPlusOneProcessing/tPlusOneProcessingSlice";
import useTPlusOneProcessing from "../hooks/useTPlusOneProcessing";
import TPlusOneProcessingBatchHeader from "./TPlusOneProcessingBatchHeader";
import { useAppSelector, useAppDispatch } from "app/hooks";

const BatchTableContainer = styled.div`
  .ant-table-content {
    border-radius: 4px;
  }

  colgroup {
    border-radius: 4px;
    col {
      border-radius: 4px;
    }
  }
`;

const TPlusOneProcessingBatchTable: React.FC<any> = () => {
  const dispatch = useAppDispatch();
  const {
    approver,
    isPushToNIBSSModalOpen,
    isRefreshStatusModalOpen,
    isApproveBulkBatchModalOpen,
    isApproveSingleBatchModalOpen,
    isChangeBulkBatchApproverModalOpen,
    isChangeSingleBatchApproverModalOpen,
  } = useAppSelector((state) => state.tPlusOneProcessing);
  const {
    form,
    batchId,
    navigate,
    setBatchId,
    summaryBatch,
    userPermission,
    onSelectChange,
    selectedBatchRows,
    handleTableChange,
    approveBatchResult,
    updateApproverResult,
    handleRefreshTRXNStatus,
    refreshTRXNStatusResult,
    pushAllBatchToNIBSSResult,
    pushSingleBatchToNIBSSResult,
    refreshSingleBatchStatusResult,
    isRefreshSingleBatchStatusOpen,
    isPushSingleBatchToNIBSSModalOpen,
    handlePushTRXNToNIBSS,
    handleApproveBulkBatch,
    handleApproveSingleBatch,
    handlePushSingleBatchToNIBSS,
    handleUpdateBulkBatchApprover,
    handleRefreshSingleBatchStatus,
    handleUpdateSingleBatchApprover,
  } = useTPlusOneProcessing();

  useEffect(() => {
    dispatch(
      updateLayout({
        title: "T + 1 Processing - Transaction Batch",
        function: "HOME",
        path: "",
      }),
    );
  }, [dispatch]);

  const columns: ColumnsType<BatchSummaryInput> = [
    {
      title: "Batch ID",
      dataIndex: "batchID",
      key: "batchID",
      width: "150px",
    },

    {
      title: "Total Debit",
      dataIndex: "computedTotalDebit",
      key: "computedTotalDebit",
      width: "120px",
      align: "right",
    },

    {
      title: "Total Credit",
      dataIndex: "computedTotalCredit",
      key: "computedTotalCredit",
      width: "120px",
      align: "right",
    },

    {
      title: "Extraction Date",
      dataIndex: "extractionTime",
      key: "extractionTime",
      width: "18%",
      align: "right",
      render: (text: string, record) => dayjs(record.extractionTime).format("YYYY-MM-DD HH:mm A"),
    },

    {
      title: "POS TXN Date",
      dataIndex: "posTransactionDate",
      key: "posTransactionDate",
      width: "12%",
      align: "center",
    },

    {
      title: "Response",
      dataIndex: "responseCode",
      key: "responseCode",
      width: "70px",
      align: "center",
      render: (_: unknown, record: BatchSummaryDTO) =>
        record.responseMessage ? (
          <Popover
            placement="left"
            overlayStyle={{
              fontSize: "14px",
              maxWidth: "200px",
              textTransform: "lowercase",
            }}
            overlayInnerStyle={{
              fontSize: "13px",
              maxWidth: "200px",
              textTransform: "lowercase",
            }}
            title={record.responseCode}
            content={record.responseMessage}
          >
            <span style={{ cursor: "pointer" }}> {record.responseCode} </span>
          </Popover>
        ) : (
          record.responseCode
        ),
    },

    {
      key: "status",
      title: "Batch Status",
      dataIndex: "status",
      width: "100px",
      align: "right",
      render: (value) => <Generic status={value} />,
    },

    {
      title: "NIBSS Status",
      dataIndex: "nibbsStatus",
      key: "nibssStatus",
      width: "100px",
      align: "right",
      render: (value) => <BatchNIBSS status={value} />,
    },

    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      align: "center",
      width: "80px",

      render: (text, record) => {
        const isPushed = record?.status === "PUSHED";
        const isApproved = record?.status === "APPROVED_AWAITING_PUSH";
        const isAwaitingApproval = record?.status === "AWAITING_APPROVAL";
        const isLineManager = userPermission.data?.email === record?.requesterLineManager;

        const canPush = isApproved;
        const canRefresh = isPushed;
        const canApprove = isAwaitingApproval && isLineManager;

        return (
          <Dropdown
            onOpenChange={() => {
              dispatch(setApprover(record?.requesterLineManager || ""));
            }}
            menu={{
              items: [
                {
                  key: "view",
                  label: "View",
                  onClick: () => navigate(`/t-plus-one-processing/${record.batchID}`),
                },
                {
                  key: "approve",
                  label: "Approve",
                  disabled: !canApprove,
                  onClick: () => {
                    dispatch(setIsApproveSingleBatchModalOpen(true));
                    setBatchId(record.batchID);
                  },
                },
                {
                  key: "push",
                  label: "Push",
                  disabled: !canPush,
                  onClick: () => {
                    dispatch(setIsPushSingleBatchToNIBSSModalOpen(true));
                    setBatchId(record.batchID);
                  },
                },
                {
                  key: "refresh",
                  label: "Refresh",
                  disabled: !canRefresh,
                  onClick: () => {
                    dispatch(setIsRefreshSingleBatchStatusOpen(true));
                    setBatchId(record.batchID);
                  },
                },

                {
                  key: "changeApprover",
                  label: "Change Approver",
                  disabled: !isAwaitingApproval,
                  onClick: () => {
                    dispatch(setIsChangeSingleBatchApproverModalOpen(true));
                    setBatchId(record.batchID);
                  },
                },
              ],
            }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <MoreOutlined
              style={{
                fontSize: "22px",
                cursor: "pointer",
                color: "#1890ff",
              }}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <BatchTableContainer>
      <Helmet>
        <title>NEFT Settlement | Batch</title>
        <meta name="description" content="Helmet application" />
      </Helmet>

      {/* header functionality for TPlusOneProcessingBatch */}
      <TPlusOneProcessingBatchHeader />

      <ConfigProvider
        renderEmpty={() =>
          summaryBatch.isError ? (
            <ApiErrorTableComponent queryResult={summaryBatch} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        }
      >
        <Table
          key="uuid"
          scroll={{ x: "auto" }}
          columns={columns as any}
          onChange={handleTableChange}
          loading={summaryBatch.isFetching}
          rowKey={(record: BatchSummaryDTO) => record?.batchID || ""}
          rowSelection={{ selectedRowKeys: selectedBatchRows, onChange: onSelectChange }}
          dataSource={summaryBatch.isError ? [] : summaryBatch.data?.response?.content}
          pagination={{
            showTitle: true,
            showSizeChanger: true,
            showQuickJumper: true,
            showPrevNextJumpers: true,
            pageSize: summaryBatch.data?.response.size,
            pageSizeOptions: [5, 10, 20, 50, 100],
            total: summaryBatch.data?.response?.totalElements,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            current:
              summaryBatch.data?.response?.number === 0
                ? 1
                : summaryBatch.data?.response?.number && summaryBatch.data?.response?.number + 1,
          }}
        />
      </ConfigProvider>

      <Modal
        okText="Yes"
        maskClosable={false}
        title="Push Batch To NIBSS"
        open={isPushSingleBatchToNIBSSModalOpen}
        onOk={() => handlePushSingleBatchToNIBSS(batchId)}
        closable={!pushSingleBatchToNIBSSResult.isLoading}
        confirmLoading={pushSingleBatchToNIBSSResult.isLoading}
        onCancel={() => dispatch(setIsPushSingleBatchToNIBSSModalOpen(false))}
        cancelButtonProps={{
          disabled: pushSingleBatchToNIBSSResult.isLoading,
        }}
      >
        <Typography.Text style={{ fontSize: "14px" }}>
          Are you sure you want to push batch with ID of {batchId} to NIBSS
        </Typography.Text>
      </Modal>

      <Modal
        okText="Yes"
        maskClosable={false}
        title="Refresh Batch Status"
        open={isRefreshSingleBatchStatusOpen}
        onOk={() => handleRefreshSingleBatchStatus(batchId)}
        closable={!refreshSingleBatchStatusResult.isLoading}
        confirmLoading={refreshSingleBatchStatusResult.isLoading}
        onCancel={() => dispatch(setIsRefreshSingleBatchStatusOpen(false))}
        cancelButtonProps={{
          disabled: refreshSingleBatchStatusResult.isLoading,
        }}
      >
        <Typography.Text style={{ fontSize: "14px" }}>
          Are you sure you want to refresh status of batch with ID of {batchId}
        </Typography.Text>
      </Modal>

      <Modal
        okText="Yes"
        maskClosable={false}
        title="Refresh Transaction Status"
        visible={isRefreshStatusModalOpen}
        onOk={() => handleRefreshTRXNStatus()}
        closable={!refreshTRXNStatusResult.isLoading}
        confirmLoading={refreshTRXNStatusResult.isLoading}
        onCancel={() => dispatch(setIsRefreshStatusModalOpen(false))}
        cancelButtonProps={{
          disabled: refreshTRXNStatusResult.isLoading,
        }}
      >
        <Typography.Text style={{ fontSize: "14px" }}>
          Are you sure you want to refresh transaction status
        </Typography.Text>
      </Modal>

      <Modal
        width={550}
        okText="Submit"
        maskClosable={false}
        onOk={() => form.submit()}
        closable={!approveBatchResult.isLoading}
        confirmLoading={approveBatchResult.isLoading}
        open={isApproveSingleBatchModalOpen || isApproveBulkBatchModalOpen}
        title={`Approve ${isApproveSingleBatchModalOpen ? "Single" : "Bulk"} Batch Transactions`}
        onCancel={() => {
          dispatch(setIsApproveBulkBatchModalOpen(false));
          dispatch(setIsApproveSingleBatchModalOpen(false));
        }}
        cancelButtonProps={{
          disabled: approveBatchResult.isLoading,
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: "Approved" }}
          onFinish={(values) => {
            if (isApproveSingleBatchModalOpen) handleApproveSingleBatch(batchId, values);
            if (isApproveBulkBatchModalOpen) handleApproveBulkBatch(selectedBatchRows, values);
          }}
        >
          <Typography.Text
            style={{
              fontSize: "14px",
              marginBottom: "20px",
              display: "block",
            }}
          >
            You are about to approve{" "}
            {isApproveSingleBatchModalOpen && (
              <>
                batch with ID <code>{batchId}</code>
              </>
            )}
            {isApproveBulkBatchModalOpen && (
              <>
                batches with IDs <br /> <code>{selectedBatchRows.join(", ")}</code>
              </>
            )}
            , kindly provide comment
          </Typography.Text>

          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="Approved"> Approve</Radio>
              <Radio value="Declined">Decline </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="comment" label="Comment" rules={[{ required: true }]}>
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        okText="Yes"
        maskClosable={false}
        open={isPushToNIBSSModalOpen}
        title="Push Transaction to NIBSS"
        onOk={() => handlePushTRXNToNIBSS()}
        closable={!pushAllBatchToNIBSSResult.isLoading}
        confirmLoading={pushAllBatchToNIBSSResult.isLoading}
        onCancel={() => dispatch(setIsPushToNIBSSModalOpen(false))}
        cancelButtonProps={{
          disabled: pushAllBatchToNIBSSResult.isLoading,
        }}
      >
        <Typography.Text style={{ fontSize: "14px" }}>
          Are you sure you want to push transaction to NIBSS
        </Typography.Text>
      </Modal>

      {/* modal to change approver - the default is line manager  */}
      <Modal
        width={550}
        okText="Submit"
        maskClosable={false}
        onOk={() => form.submit()}
        closable={!updateApproverResult.isLoading}
        confirmLoading={updateApproverResult.isLoading}
        open={isChangeSingleBatchApproverModalOpen || isChangeBulkBatchApproverModalOpen}
        title={`Change Approver for ${
          isChangeSingleBatchApproverModalOpen ? "Single" : "Bulk"
        } Batch Transactions`}
        onCancel={() => {
          dispatch(setIsChangeBulkBatchApproverModalOpen(false));
          dispatch(setIsChangeSingleBatchApproverModalOpen(false));
        }}
        cancelButtonProps={{
          disabled: updateApproverResult.isLoading,
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ approver: approver }}
          onFinish={(values) => {
            if (isChangeSingleBatchApproverModalOpen)
              handleUpdateSingleBatchApprover(batchId, values);
            if (isChangeBulkBatchApproverModalOpen)
              handleUpdateBulkBatchApprover(selectedBatchRows, values);
          }}
        >
          <Typography.Text
            style={{
              fontSize: "14px",
              marginBottom: "20px",
              display: "block",
            }}
          >
            You are about to change approver of{" "}
            {isChangeSingleBatchApproverModalOpen && (
              <>
                batch with ID <code>{batchId}</code>
              </>
            )}
            {isChangeBulkBatchApproverModalOpen && (
              <>
                batches with IDs <br /> <code>{selectedBatchRows.join(", ")}</code>
              </>
            )}
          </Typography.Text>

          <Form.Item
            name="approver"
            label="Approver"
            rules={[
              { required: true, message: "Approver email is required" },
              {
                validator: (_, value) => {
                  if (value === userPermission.data?.email) {
                    return Promise.reject(new Error("Cannot assign to current user"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="firstname.lastname@interswitchgroup.com" />
          </Form.Item>
        </Form>
      </Modal>
    </BatchTableContainer>
  );
};

export default TPlusOneProcessingBatchTable;
